import React, { useCallback, useEffect, useRef, useState } from "react";
import DataTable from "react-data-table-component";
import Header from "../../common/Header";
import Sidebar from "../../common/Sidebar";
import Footer from "../../common/Footer";
import PageHeader from "../../pages/PageHeader";
import Loader from "../../common/Loader";
import { AdminHelpCenter } from "../../utils/APIClient";
import { triggerAlert } from "../../utils";
import { confirm } from "../../common/ConfirmDialog";
import { helpTableStyles } from "./tableStyles";

/**
 * Help Center → Videos.
 *
 * Upload goes browser → Django → S3, and the row is only created once S3
 * has the file, so the list never shows a video that will not play. Because
 * the file can be up to 200MB, the request reports real upload progress —
 * a plain spinner here looks like a hang.
 *
 * `duration_seconds` is read in the browser from a temporary <video>
 * element and posted with the form; the backend does not shell out to
 * ffmpeg.
 */

const MAX_VIDEO_MB = 200;
const MAX_THUMB_MB = 2;
const PAGE_SIZE = 25;

const EMPTY_FORM = {
  title: "",
  description: "",
  keywords: "",
  category: "",
  status: "draft",
  is_pinned: false,
  sort_order: "",
};

/** Read a video file's duration client-side; resolves 0 if unreadable. */
const readDuration = (file) => new Promise((resolve) => {
  try {
    const el = document.createElement("video");
    el.preload = "metadata";
    const url = URL.createObjectURL(file);
    const done = (value) => { URL.revokeObjectURL(url); resolve(value); };
    el.onloadedmetadata = () => done(
      Number.isFinite(el.duration) ? Math.round(el.duration) : 0,
    );
    el.onerror = () => done(0);
    el.src = url;
  } catch (e) { resolve(0); }
});

const formatDuration = (seconds) => {
  if (!seconds) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

const formatSize = (bytes) => {
  if (!bytes) return "—";
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
};

export default function HelpVideos() {
  document.title = "Digital Employees — Help Center Videos";

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  // Set while editing to clear an existing poster; a newly picked file wins.
  const [removeThumb, setRemoveThumb] = useState(false);
  const videoRef = useRef(null);
  const thumbRef = useRef(null);

  const fetchCategories = useCallback(async () => {
    try {
      const r = await AdminHelpCenter.listCategories();
      setCategories(r?.data?.results || []);
    } catch (e) { setCategories([]); }
  }, []);

  const fetchRows = useCallback(async (pageNo = 1) => {
    setLoading(true);
    try {
      const r = await AdminHelpCenter.listVideos({
        limit: PAGE_SIZE,
        offset: (pageNo - 1) * PAGE_SIZE,
        q: search.trim() || undefined,
        status: statusFilter || undefined,
      });
      const list = r?.data?.results || [];
      list.forEach((v, i) => { v.Num = (pageNo - 1) * PAGE_SIZE + i + 1; });
      setRows(list);
      setTotal(r?.data?.count || 0);
    } catch (e) {
      setRows([]); setTotal(0);
      triggerAlert("error", "Oops...", "Failed to load help videos.");
    } finally { setLoading(false); }
  }, [search, statusFilter]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  // Debounce so typing in the search box doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); fetchRows(1); }, 300);
    return () => clearTimeout(t);
  }, [fetchRows]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditing(null);
    setProgress(0);
    setRemoveThumb(false);
    if (videoRef.current) videoRef.current.value = "";
    if (thumbRef.current) thumbRef.current.value = "";
  };

  const setField = (key) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const startEdit = (row) => {
    setEditing(row);
    setForm({
      title: row.title || "",
      description: row.description || "",
      keywords: row.keywords || "",
      category: String(row.category || ""),
      status: row.status || "draft",
      is_pinned: !!row.is_pinned,
      sort_order: String(row.sort_order ?? ""),
    });
    if (videoRef.current) videoRef.current.value = "";
    if (thumbRef.current) thumbRef.current.value = "";
    setRemoveThumb(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const videoFile = videoRef.current?.files?.[0] || null;
    const thumbFile = thumbRef.current?.files?.[0] || null;

    if (!form.title.trim()) { triggerAlert("error", "Oops...", "Enter a title."); return; }
    if (!form.category) { triggerAlert("error", "Oops...", "Pick a category."); return; }
    if (!editing && !videoFile) { triggerAlert("error", "Oops...", "Choose a video file."); return; }
    // Checked again server-side; this just avoids a doomed 200MB upload.
    if (videoFile && videoFile.size > MAX_VIDEO_MB * 1024 * 1024) {
      triggerAlert("error", "Oops...", `Video must be ${MAX_VIDEO_MB}MB or smaller.`);
      return;
    }
    if (thumbFile && thumbFile.size > MAX_THUMB_MB * 1024 * 1024) {
      triggerAlert("error", "Oops...", `Thumbnail must be ${MAX_THUMB_MB}MB or smaller.`);
      return;
    }

    setSaving(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("title", form.title.trim());
      fd.append("description", form.description.trim());
      fd.append("keywords", form.keywords.trim());
      fd.append("category", form.category);
      fd.append("status", form.status);
      fd.append("is_pinned", form.is_pinned ? "true" : "false");
      fd.append("sort_order", String(Number(form.sort_order) || 0));
      if (videoFile) {
        fd.append("video", videoFile);
        fd.append("duration_seconds", String(await readDuration(videoFile)));
      }
      if (thumbFile) fd.append("thumbnail", thumbFile);
      if (editing && removeThumb && !thumbFile) fd.append("remove_thumbnail", "true");

      if (editing) {
        await AdminHelpCenter.updateVideo(editing.id, fd, setProgress);
        triggerAlert("success", "Saved", "Help video updated.");
      } else {
        await AdminHelpCenter.createVideo(fd, setProgress);
        triggerAlert("success", "Uploaded", "Help video added.");
      }
      resetForm();
      fetchRows(page);
    } catch (err) {
      const msg = err?.response?.data?.message
        || err?.response?.data?.detail
        || "Upload failed.";
      triggerAlert("error", "Oops...", msg);
    } finally { setSaving(false); setProgress(0); }
  };

  const togglePublished = async (row) => {
    const fd = new FormData();
    fd.append("status", row.status === "published" ? "draft" : "published");
    try {
      await AdminHelpCenter.updateVideo(row.id, fd);
      fetchRows(page);
    } catch (e) { triggerAlert("error", "Oops...", "Could not update."); }
  };

  const onDelete = async (row) => {
    const ok = await confirm({
      title: "Delete help video?",
      message: `Delete "${row.title}"? The video file is removed from storage too.`,
      confirmLabel: "Delete",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await AdminHelpCenter.deleteVideo(row.id);
      triggerAlert("success", "Deleted", "Help video removed.");
      if (editing?.id === row.id) resetForm();
      fetchRows(page);
    } catch (e) { triggerAlert("error", "Oops...", "Delete failed."); }
  };

  const columns = [
    { name: "S.No", width: "70px", selector: (r) => r.Num, cell: (r) => <span>{r.Num}</span> },
    {
      name: "Video", width: "130px",
      cell: (r) => (r.thumbnail_url
        ? <img src={r.thumbnail_url} alt="" style={{ width: 104, height: 58, objectFit: "cover", borderRadius: 4 }} />
        : <video src={r.video_url} muted preload="metadata" style={{ width: 104, height: 58, objectFit: "cover", borderRadius: 4, background: "#000" }} />),
    },
    {
      name: "Title", sortable: true, selector: (r) => r.title, grow: 2,
      cell: (r) => (
        <div style={{ padding: "6px 0" }}>
          <div style={{ fontWeight: 600 }}>{r.title}</div>
          {r.keywords ? (
            <div className="text-muted" style={{ fontSize: 12 }}>{r.keywords}</div>
          ) : null}
        </div>
      ),
    },
    { name: "Category", sortable: true, selector: (r) => r.category_name, width: "140px" },
    { name: "Length", width: "100px", selector: (r) => r.duration_seconds, cell: (r) => formatDuration(r.duration_seconds) },
    { name: "Size", width: "100px", selector: (r) => r.file_size_bytes, cell: (r) => formatSize(r.file_size_bytes) },
    { name: "Views", width: "90px", sortable: true, selector: (r) => r.view_count },
    {
      name: "Status", width: "120px", sortable: true, selector: (r) => r.status,
      cell: (r) => (
        <div className="d-flex flex-column gap-1">
          {r.status === "published"
            ? <span className="badge bg-success">Published</span>
            : <span className="badge bg-secondary">Draft</span>}
          {r.is_pinned ? <span className="badge bg-info">Pinned</span> : null}
        </div>
      ),
    },
    {
      name: "Action", width: "230px",
      cell: (r) => (
        <div className="d-flex gap-2">
          <button className="btn btn-outline-primary btn-sm" onClick={() => startEdit(r)}>Edit</button>
          <button
            className={`btn btn-sm ${r.status === "published" ? "btn-outline-warning" : "btn-outline-success"}`}
            onClick={() => togglePublished(r)}
          >
            {r.status === "published" ? "Unpublish" : "Publish"}
          </button>
          <button className="btn btn-outline-danger btn-sm" onClick={() => onDelete(r)}>Delete</button>
        </div>
      ),
    },
  ];

  return (
    <div id="layout-wrapper">
      <Header />
      <Sidebar />
      <div className="main-content">
        <div className="page-content" style={{ backgroundColor: "#ededed" }}>
          <div className="container-fluid">
            <PageHeader mainHeading="Help Center Videos" heading="Help Center / Videos" />
            <div className="row"><div className="col-12"><div className="card"><div className="card-body">
              <div className="alert alert-info py-2 mb-3">
                Upload MP4, MOV or WebM up to {MAX_VIDEO_MB}MB. Customers only see
                <strong> Published </strong> videos. <strong>Keywords</strong> are the
                cheapest way to improve search — list the words customers would type
                ("OTP", "2FA") even when the video uses different wording.
              </div>

              {categories.length === 0 && (
                <div className="alert alert-warning py-2 mb-3">
                  No categories exist yet. Add one on the Help Center Categories page first.
                </div>
              )}

              <form onSubmit={onSubmit} className="row g-2 mb-4">
                <div className="col-md-6">
                  <label className="form-label">Title</label>
                  <input
                    className="form-control" maxLength={200} placeholder="e.g. How to download your invoice"
                    value={form.title} onChange={setField("title")}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Category</label>
                  <select className="form-select" value={form.category} onChange={setField("category")}>
                    <option value="">Select…</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Status</label>
                  <select className="form-select" value={form.status} onChange={setField("status")}>
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                  </select>
                </div>

                <div className="col-md-6">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control" rows={3}
                    placeholder="One or two sentences explaining what the video covers."
                    value={form.description} onChange={setField("description")}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Search keywords <span className="text-muted">(comma separated)</span>
                  </label>
                  <textarea
                    className="form-control" rows={3}
                    placeholder="invoice, receipt, statement, billing"
                    value={form.keywords} onChange={setField("keywords")}
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    Video file {editing ? <span className="text-muted">(leave empty to keep the current one)</span> : null}
                  </label>
                  <input type="file" ref={videoRef} accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov,.m4v" className="form-control" />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Thumbnail <span className="text-muted">(optional)</span></label>
                  <input
                    type="file" ref={thumbRef} accept="image/png,image/jpeg,image/webp"
                    className="form-control"
                    onChange={() => setRemoveThumb(false)}
                  />
                  {/* Only meaningful while editing: shows what is stored now and
                      lets the admin clear it. With no thumbnail both portals fall
                      back to the video's own first frame. */}
                  {editing && editing.thumbnail_url && !removeThumb && (
                    <div className="d-flex align-items-center gap-2 mt-2">
                      <img
                        src={editing.thumbnail_url} alt="Current thumbnail"
                        style={{ width: 72, height: 40, objectFit: "cover", borderRadius: 4, border: "1px solid #dee2e6" }}
                      />
                      <span className="text-muted small flex-grow-1">Current thumbnail</span>
                      <button
                        type="button" className="btn btn-outline-danger btn-sm"
                        onClick={() => {
                          setRemoveThumb(true);
                          if (thumbRef.current) thumbRef.current.value = "";
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                  {editing && removeThumb && (
                    <div className="d-flex align-items-center gap-2 mt-2">
                      <span className="text-danger small flex-grow-1">
                        Thumbnail will be removed when you save.
                      </span>
                      <button
                        type="button" className="btn btn-outline-secondary btn-sm"
                        onClick={() => setRemoveThumb(false)}
                      >
                        Undo
                      </button>
                    </div>
                  )}
                </div>
                <div className="col-md-2">
                  <label className="form-label">Sort order</label>
                  <input type="number" min="0" className="form-control" placeholder="0"
                    value={form.sort_order} onChange={setField("sort_order")} />
                </div>
                <div className="col-md-2 d-flex align-items-end">
                  <div className="form-check mb-2">
                    <input className="form-check-input" type="checkbox" id="hc-pinned"
                      checked={form.is_pinned} onChange={setField("is_pinned")} />
                    <label className="form-check-label" htmlFor="hc-pinned">Pin to top</label>
                  </div>
                </div>

                {saving && progress > 0 && (
                  <div className="col-12">
                    <div className="progress" style={{ height: 6 }}>
                      <div className="progress-bar" role="progressbar" style={{ width: `${progress}%` }} />
                    </div>
                    <div className="text-muted small mt-1">
                      {progress < 100 ? `Uploading… ${progress}%` : "Storing the video…"}
                    </div>
                  </div>
                )}

                <div className="col-12 d-flex justify-content-end gap-2">
                  {editing && (
                    <button type="button" className="btn btn-outline-secondary" onClick={resetForm} disabled={saving}>
                      Cancel edit
                    </button>
                  )}
                  <button type="submit" className="btn btn-primary" disabled={saving || categories.length === 0}>
                    {saving ? "Saving…" : editing ? "Update video" : "Upload video"}
                  </button>
                </div>
              </form>

              <div className="row g-2 mb-3">
                <div className="col-md-6">
                  <input
                    className="form-control" placeholder="Search titles, descriptions and keywords…"
                    value={search} onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="">All statuses</option>
                    <option value="published">Published</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>
              </div>

              {!loading ? (
                <DataTable
                  columns={columns} data={rows} persistTableHead fixedHeader
                  fixedHeaderScrollHeight="640px" customStyles={helpTableStyles}
                  pagination paginationServer paginationTotalRows={total}
                  paginationPerPage={PAGE_SIZE}
                  paginationRowsPerPageOptions={[PAGE_SIZE]}
                  onChangePage={(p) => { setPage(p); fetchRows(p); }}
                  noDataComponent={<div style={{ padding: 24 }}>No help videos yet. Upload one above.</div>}
                />
              ) : <div className="loader-overlay text-white"><Loader /></div>}
            </div></div></div></div>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
}
